import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { LocalizationScopeDirective, LocalizePipe } from '@capital-access/common/localization';
import { NOTIFICATIONS_LOCALIZATION_SCOPE } from '../../constants/notifications-drawer.constants';
import {
  NotificationDto,
  parseNotificationBody,
  ReportingNotificationBody
} from '../../models/notifications-grouped.model';
import { NotificationsHubService } from '../../services/notifications-hub.service';
import { selectDeletingNotificationIds } from '../../+store/notifications-drawer.selectors';

@Component({
  selector: 'ca-reporting-notification-item',
  templateUrl: './reporting-notification-item.component.html',
  styleUrls: ['./reporting-notification-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [LocalizationScopeDirective, LocalizePipe]
})
export class ReportingNotificationItemComponent {
  notification = input.required<NotificationDto>();
  markAsRead = output<number>();
  delete = output<number>();

  readonly localizationScope = NOTIFICATIONS_LOCALIZATION_SCOPE;

  private service = inject(NotificationsHubService);
  private store = inject(Store);
  private deletingIds = toSignal(this.store.select(selectDeletingNotificationIds), { initialValue: [] as number[] });

  readonly body = computed(() => parseNotificationBody<ReportingNotificationBody>(this.notification().body));
  readonly hasError = computed(() => !!this.body()?.errorMessage);
  readonly subtitle = computed(() => {
    const b = this.body();
    if (!b) return '';
    const parts: string[] = [];
    if (b.reportType) parts.push(b.reportType);
    if (b.fileSize) parts.push(b.fileSize);
    if (b.generatedDate) parts.push(b.generatedDate);
    return parts.join(', ');
  });
  readonly hasDownload = computed(() => !!this.body()?.sourceEntityId);
  readonly deleting = computed(() => this.deletingIds().includes(this.notification().notificationId));

  onMarkAsRead(): void {
    this.markAsRead.emit(this.notification().notificationId);
  }

  onDelete(): void {
    if (this.deleting()) return;
    this.delete.emit(this.notification().notificationId);
  }

  onDownload(): void {
    const b = this.body();
    if (!b?.sourceEntityId) return;

    this.service.downloadReport(b.sourceEntityId).subscribe(blob => {
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = b.reportName ?? 'report';
      anchor.click();
      URL.revokeObjectURL(url);

      if (!this.notification().read) {
        this.markAsRead.emit(this.notification().notificationId);
      }
    });
  }
}
